import { useCallback, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import Input from "../../../../components/input";
import Button from "../../../../components/button";
import {
  SummaryChallenge,
  WeeklySummaryResponse,
} from "../../../../redux/features/weekly-summary-slice";
import { AppDispatch } from "../../../../redux/store";
import {
  ChallengeResponse,
  challengesStateAction,
  CommonStatus,
  getChallengesState,
} from "../../../../redux/features/other-slice";
import { OptionValue } from "../../../../types/component";
import { isSelectSingleValue } from "../../../../core/utils";
import { getUserState } from "../../../../redux/features/auth-slice";
import { InfoOutlined } from "../../../../components/icons/info-outlined";
import StyledSelect from "../../../../components/styled-select";
import SummaryPropertyDiscussDialog from "../summary-property-discuss-dialog";
import { TrashOutlined } from "../../../../components/icons/trash-outlined";
import { CheckOutlined } from "../../../../components/icons/check-outlined";
import { EditOutlined } from "../../../../components/icons/edit-outlined";

interface Props {
  summary: WeeklySummaryResponse | null;
  challenges: SummaryChallenge[];
  setChallenges: (values: SummaryChallenge[]) => void;
  onRefresh: () => void;
}

export default function ChallengeForm(props: Props) {
  const { summary, challenges, setChallenges, onRefresh } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const challengesState = useSelector(getChallengesState);

  const [selectedChallenge, setSelectedChallenge] =
    useState<SummaryChallenge | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<SummaryChallenge | null>(null);

  const getAvailableOptions = () => {
    const selectedTypes = challenges.map((challenge) => challenge.type.id);
    const availableChallenges = challengesState.data.filter(
      (challenge: ChallengeResponse) => !selectedTypes.includes(challenge.id)
    );
    return [
      {
        id: "null",
        title: "Nil",
        description: "Nothing",
        category: {
          id: "",
          title: "",
          status: CommonStatus.Inactive,
        },
      },
      ...availableChallenges,
    ];
  };

  const fetchChallenges = useCallback(async () => {
    if (user) {
      await dispatch(
        challengesStateAction({
          categoryId: user.category.id,
        })
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return (
    <>
      <div className="flex flex-col rounded-lg space-y-4">
        {challenges.map((chal: SummaryChallenge) => {
          const isEditing = editId === chal.type.id;
        return (
          <div
            key={chal.type.id}
            className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
          >
            <div className="w-full flex flex-col space-y-4">
              <div>
                <label className="text-xs text-text-title">Type</label>
                {isEditing ? (
                  <div>
                    <StyledSelect
                      loading={challengesState.loading}
                      menuPosition="fixed"
                      options={getAvailableOptions().map(
                        (challenge: ChallengeResponse) => ({
                          value: challenge.id,
                          label: challenge.title,
                        })
                      )}
                      isClearable={false}
                      value={
                        editValues &&
                        editValues.type.id &&
                        editValues.type.title
                          ? {
                              value: editValues.type.id,
                              label: editValues.type.title,
                            }
                          : undefined 
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setEditValues({
                            ...editValues!,
                            type: {
                              id: value.value as string,
                              title: value.label as string,
                            },
                            explain:
                              (value.value as string) === "null" ? "--" : "",
                            action:
                              (value.value as string) === "null" ? "--" : "",
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                    {editValues?.type.id && (
                      <label className="flex items-center text-text-title text-xs mt-1">
                        <InfoOutlined className="h-4 w-4 mr-1" />
                        {editValues.type.id === "null"
                          ? "Nothing"
                          : challengesState.data.find(
                              (e) => e.id === editValues.type.id
                            )?.description || ""}
                      </label>
                    )}
                  </div>
                ) : (
                  <Input type="text" value={chal.type.title} readonly />
                )}
              </div>
              <div>
                <label className="text-xs text-text-title">Explain</label>
                <Input
                  type="text"
                  inputType="textarea"
                  rows={3}
                  value={isEditing ? editValues?.explain : chal.explain}
                  readonly={!isEditing}
                  onChange={(event) => {
                    if (isEditing) {
                      setEditValues({
                        ...editValues!,
                        explain: event.target.value,
                      });
                    }
                  }}
                  errorText={
                    isEditing && editValues?.explain === ""
                      ? "This field is required"
                      : undefined
                  }
                />
              </div>
              <div>
                <label className="text-xs text-text-title">
                  Action / Proposed
                </label>
                <Input
                  type="text"
                  inputType="textarea"
                  rows={3}
                  value={isEditing ? editValues?.action : chal.action}
                  readonly={!isEditing}
                  onChange={(event) => {
                    if (isEditing) {
                      setEditValues({
                        ...editValues!,
                        action: event.target.value,
                      });
                    }
                  }}
                  errorText={
                    isEditing && editValues?.action === ""
                      ? "This field is required"
                      : undefined
                  }
                />
              </div>
            </div>
            <div className="w-full flex items-center justify-end space-x-2">
              {summary &&
                summary.challenges.findIndex(
                  (e) =>
                    e.type.id === chal.type.id &&
                    e.explain === chal.explain &&
                    e.action === chal.action
                ) !== -1 && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${chal.discuss.length})`}
                    onClick={() => setSelectedChallenge(chal)}
                    exClassName="w-fit border-divider"
                  />
                )}
              <div className="flex flex-1 items-center justify-end space-x-2">
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    if (isEditing) {
                      // Save logic
                      if (
                        editValues?.type.id &&
                        editValues.type.title &&
                        editValues.explain &&
                        editValues.action
                      ) {
                        const editIndex = challenges.findIndex(
                          (e) =>
                            e.type.id === chal.type.id &&
                            e.explain === chal.explain &&
                            e.action === chal.action
                        );
                        if (editIndex !== -1) {
                          const tmp = [...challenges];
                          tmp[editIndex] = {
                            ...tmp[editIndex],
                            type: editValues.type,
                            explain: editValues.explain,
                            action: editValues.action,
                          };
                          setChallenges(tmp);
                          setEditId(null);
                          setEditValues(null);
                        }
                      }
                    } else {
                      // Enter edit mode
                      setEditId(chal.type.id);
                      setEditValues(chal);
                    }
                  }}
                >
                  {isEditing ? (
                    <CheckOutlined className="h-4 w-4 text-success-main" />
                  ) : (
                    <EditOutlined className="h-4 w-4" />
                  )}
                </div>
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    const removedIndex = challenges.findIndex(
                      (e) =>
                        e.type.id === chal.type.id &&
                        e.explain === chal.explain &&
                        e.action === chal.action
                    );
                    if (removedIndex !== -1) {
                      const tmp = challenges.filter(
                        (_, index) => index !== removedIndex
                      );
                      setChallenges(tmp);
                    }
                  }}
                >
                  <TrashOutlined className="h-4 w-4 text-error-main" />
                </div>
              </div>
            </div>
          </div>
        );
        })}
        {challenges.findIndex((e) => e.type.id === "null") === -1 && (
          <Formik
            initialValues={{ id: "", title: "", explain: "", action: "" }}
            validationSchema={Yup.object().shape({
              id: Yup.string().required("This field is required"),
              title: Yup.string().required("This field is required"),
              explain: Yup.string().required("This field is required"),
              action: Yup.string().required("This field is required"),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(false);
              setChallenges([
                ...challenges,
                {
                  type: { id: values.id, title: values.title },
                  explain: values.explain,
                  action: values.action,
                  discuss: [],
                },
              ]);
              resetForm();
            }}
          >
            {({ values, setValues, errors, handleChange, handleSubmit }) => (
              <Form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="flex flex-col items-center space-y-4 border border-divider rounded-lg p-4"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                      <StyledSelect
                        loading={challengesState.loading}
                        menuPosition="fixed"
                        options={getAvailableOptions().map(
                          (challenge: ChallengeResponse) => ({
                            value: challenge.id,
                            label: challenge.title,
                          })
                        )}
                        isClearable={false}
                        value={
                          values.id !== "" && values.title !== ""
                            ? { value: values.id, label: values.title }
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value: SingleValue<OptionValue> | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              id: value.value as string,
                              title: value.label as string,
                              explain: value.value === "null" ? "--" : "",
                              action: value.value === "null" ? "--" : "",
                            });
                          }
                        }}
                        exClassName="w-full"
                      />
                      {values.id && (
                        <label className="flex items-center text-text-title text-xs mt-1">
                          <InfoOutlined className="h-4 w-4 mr-1" />
                          {values.id === "null"
                            ? "Nothing"
                            : challengesState.data.find((e) => e.id === values.id)
                              ?.description || ""}
                        </label>
                      )}
                  </div>
                  <div>
                    <label className="text-xs text-text-title">Explain</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      id="explain"
                      value={values.explain}
                      onChange={handleChange}
                      readonly={values.id === "null"}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-title">
                      Action / Proposed
                    </label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      id="action"
                      value={values.action}
                      onChange={handleChange}
                      readonly={values.id === "null"}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  label="Add"
                  exClassName="w-fit self-end"
                  disabled={
                    Array.isArray(errors) ||
                    Object.values(errors).toString() !== "" || values.id === ""
                  }
                />
              </Form>
            )}
          </Formik>
          )}
      </div>
      {summary && (
        <SummaryPropertyDiscussDialog
          summaryId={summary.id}
          type="challenge"
          property={selectedChallenge}
          setProperty={setSelectedChallenge}
          onSubmitted={onRefresh}
        />
      )}
    </>
  );
}
